import apiClient from '@/lib/api-client';
import type { User } from '@/types/auth';
import type {
  Organization,
  OrganizationRegistration,
  OrganizationRegistrationResponse,
  OnboardingStatusResponse,
  BusinessInfoUpdate,
  BusinessInfoUpdateResponse,
  OnboardingCompleteResponse,
  ApiResponse
} from '@/types/organization';
import type { CustomQuestion } from '@/types/booking';

interface OrganizationStats {
  totalUsers: number;
  totalBookings: number;
  totalRevenue: number;
  activeServices: number;
  activeProviders: number;
}

export interface SubdomainCheckResponse {
  available: boolean;
  reason?: 'subdomain_too_short' | 'invalid_format' | 'reserved' | 'taken';
}

class OrganizationService {
  // Public registration endpoint (no auth required)
  async register(data: OrganizationRegistration): Promise<OrganizationRegistrationResponse> {
    const response = await apiClient.post<ApiResponse<OrganizationRegistrationResponse>>('/v1/organizations', data);
    return response.data.data;
  }

  // Public endpoint to check if a subdomain is available (no auth required)
  async checkSubdomainAvailability(subdomain: string): Promise<SubdomainCheckResponse> {
    const response = await apiClient.get<ApiResponse<SubdomainCheckResponse>>(
      `/v1/organizations/check_subdomain?subdomain=${encodeURIComponent(subdomain)}`
    );
    return response.data.data;
  }

  // Onboarding endpoints (require auth)
  async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
    // response.data gives us the backend's {data: {...}, meta: {...}} object
    // We need to extract the actual payload from response.data.data
    const response = await apiClient.get<ApiResponse<OnboardingStatusResponse>>('/v1/admin/onboarding/status');
    return response.data.data;
  }

  async updateBusinessInfo(updates: BusinessInfoUpdate): Promise<BusinessInfoUpdateResponse> {
    const response = await apiClient.put<ApiResponse<BusinessInfoUpdateResponse>>('/v1/admin/onboarding/business_info', updates);
    return response.data.data;
  }

  async completeOnboarding(): Promise<OnboardingCompleteResponse> {
    const response = await apiClient.put<ApiResponse<OnboardingCompleteResponse>>('/v1/admin/onboarding/complete');
    return response.data.data;
  }

  async skipOnboarding(): Promise<{ message: string; skipped: boolean; skipped_at: string }> {
    const response = await apiClient.put<ApiResponse<{ message: string; skipped: boolean; skipped_at: string }>>('/v1/admin/onboarding/skip');
    return response.data.data;
  }

  async getOrganization(): Promise<Organization> {
    const { data } = await apiClient.get<ApiResponse<Organization>>('/v1/organization');
    return data.data;
  }

  // Alias for getOrganization
  async get(): Promise<Organization> {
    return this.getOrganization();
  }

  async updateOrganization(updates: Partial<Organization>): Promise<Organization> {
    const { data } = await apiClient.put<ApiResponse<Organization>>('/v1/organization', updates);
    return data.data;
  }

  // Alias for updateOrganization
  async update(updates: Partial<Organization>): Promise<Organization> {
    return this.updateOrganization(updates);
  }

  async updateSettings(settings: any): Promise<Organization> {
    const { data } = await apiClient.put<ApiResponse<Organization>>('/v1/organization/settings', settings);
    return data.data;
  }

  // DEPRECATED: Custom questions moved to service level
  // Use bookingService.updateServiceCustomQuestions(serviceId, customQuestions) instead
  async updateCustomQuestions(customQuestions: CustomQuestion[]): Promise<{ custom_booking_questions: CustomQuestion[]; message: string }> {
    const { data } = await apiClient.put<ApiResponse<{ custom_booking_questions: CustomQuestion[]; message: string }>>(
      '/v1/organization/custom_questions',
      { custom_booking_questions: customQuestions }
    );
    return data.data;
  }

  async getStats(): Promise<OrganizationStats> {
    const { data } = await apiClient.get<ApiResponse<OrganizationStats>>('/v1/organization/stats');
    return data.data;
  }

  // User management
  async getUsers(): Promise<User[]> {
    const { data } = await apiClient.get<{ data: any[] }>('/v1/users');
    return data.data.map(user => this.transformUser(user));
  }

  async getUser(id: number): Promise<User> {
    const { data } = await apiClient.get<{ data: User }>(`/v1/users/${id}`);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async createUser(user: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    const { data } = await apiClient.post<{ data: User }>('/v1/users', user);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async updateUser(id: number, updates: Partial<User>): Promise<User> {
    // Transform camelCase to snake_case for the request
    const snakeCaseUpdates: any = {};
    if (updates.firstName !== undefined) snakeCaseUpdates.first_name = updates.firstName;
    if (updates.lastName !== undefined) snakeCaseUpdates.last_name = updates.lastName;
    if (updates.phone !== undefined) snakeCaseUpdates.phone = updates.phone;
    if (updates.email !== undefined) snakeCaseUpdates.email = updates.email;
    if (updates.role !== undefined) snakeCaseUpdates.role = updates.role;
    if (updates.active !== undefined) snakeCaseUpdates.active = updates.active;

    const { data } = await apiClient.put<{ data: any }>(`/v1/users/${id}`, snakeCaseUpdates);
    return this.transformUser(data.data);
  }

  async toggleUserActive(id: number): Promise<User> {
    const { data } = await apiClient.post<{ data: any }>(`/v1/users/${id}/toggle_active`);
    return this.transformUser(data.data);
  }

  async changeUserRole(id: number, role: User['role']): Promise<User> {
    const { data } = await apiClient.post<{ data: any }>(`/v1/users/${id}/change_role`, { role });
    return this.transformUser(data.data);
  }

  private transformUser(user: any): User {
    return {
      id: user.id,
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      phone: user.phone,
      role: user.role,
      organizationId: user.organization_id,
      active: user.active,
      emailConfirmed: user.email_confirmed,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
    };
  }

  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/v1/users/${id}`);
  }
}

export const organizationService = new OrganizationService();